import { LoadingButtonProps } from '@mui/lab';
import { BoxProps, GridProps, PaperProps } from '@mui/material';
import React, { ReactNode } from 'react';
import {
  BrowserNativeObject,
  ControllerFieldState,
  ControllerRenderProps,
  FieldArray,
  FieldArrayPath,
  FieldPath,
  RegisterOptions,
  UseFormProps,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { E164_REGEX } from '../../config/regex';
import useGeneralHook from '../hook/useGeneralHook';
import { ArrayContainerProps } from './element/array-schema/types';
import { CheckBoxSchemaProps } from './element/checkbox/types';
import { DateRangePickerSchemaProps } from './element/date-range/types';
import { DatePickerSchemaProps } from './element/datepicker-element/types';
import { DateTimePickerSchemaProps } from './element/datepickerTime-element/types';
import { PropsDropZoneElement } from './element/drop-zone/DropZoneElement';
import { PropsMultipleCheckBoxElement } from './element/multiple-checkbox/types';
import { MultipleRadioSchemaProps } from './element/multiple-radio/types';
import countryphonecodes from './element/phone-input/countryphonecodes.json';
import { RadioSchemaProps } from './element/radio/RadioElement';
import { PropsSelectElement } from './element/select/SelectElement';
import { TextEditorElementProps } from './element/text-editor/TextEditorElement';
import { TextFieldSchemaProps } from './element/text-field/TextFieldSchema';
import { PropsTimePickerElement } from './element/timepicker-element/TimePickerElement';
import { UploadFileElementProps } from './element/uploadFile/UploadFileElement';
import { AutoCompleteSchemaProps } from './element/autocomplete/AutoCompleteSchema';
import { SwitchSchemaProps } from './element/switch/SwitchSchema';
export interface IControllerRenderProps<
  T extends FieldValues = any,
  TName extends FieldPath<T> = FieldPath<T>,
> {
  field: ControllerRenderProps<T, TName>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<T>;
}

export type ElementMode =
  | 'node'
  | 'hidden'
  | 'text'
  | 'text-field'
  | 'phone-field'
  | 'auto-complete'
  | 'select'
  | 'datePicker'
  | 'button'
  | 'drop-zone'
  | 'switch'
  | 'checkbox'
  | 'multiple-checkbox'
  | 'radio'
  | 'multiple-radio'
  | 'array'
  | 'dateTimePicker'
  | 'timePicker'
  | 'dateRange'
  | 'text-editor'
  | 'uploadImage'
  | 'uploadFile'
  | 'raw';

/*---------------------------*/

export interface ElementBaseProps<
  T extends FieldValues | boolean | string | number | symbol = any,
> {
  mode: ElementMode;
  key_element?: string;
  rules?: Omit<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  propsWrapper?: GridProps;
  defaultValue?: T;
  tooltipError?: boolean;
  noHelperText?: boolean;
  hidden?: boolean;
  unregister?: boolean;
  shouldUnregister?: boolean;
  mapperValue?: (...params) => any;
  /**
   * props này được thêm vào để đáp ứng trường hợp có validate trong object rules nhưng không muốn hiển thị dấu '*'
   */
  showRequiredAsterisk?: boolean;
}

interface IHiddenElement {
  type?: 'hiddenField';
}

export interface IDropZoneElement extends ElementBaseProps, PropsDropZoneElement {
  mode: 'drop-zone';
}
export interface INodeElement {
  mode: 'node';
  render?: (props: IControllerRenderProps) => React.ReactElement;
}
export interface IRawElement<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> {
  mode: 'raw';
  render?: (props: IControllerRenderProps<T, TName>) => React.ReactElement;
}

export interface IArrayElement<
  T extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<T> = FieldArrayPath<T>,
> extends Omit<ElementBaseProps<FieldArray<T, TFieldArrayName>[]>, 'rules' | 'defaultValue'>,
    ArrayContainerProps<T, TFieldArrayName> {
  mode: 'array';
}

export interface IButtonElement extends ElementBaseProps, Omit<LoadingButtonProps, 'defaultValue'> {
  mode: 'button';
  label?: React.ReactNode | string;
}
export interface ITextElement {
  mode: 'text';
  label?: React.ReactNode;
  boxProps: BoxProps;
}
export interface ITextFieldElement<T extends FieldValues = FieldValues>
  extends ElementBaseProps<T>,
    Omit<TextFieldSchemaProps, 'defaultValue'> {
  mode: 'text-field';
}

export interface IUploadFileElement
  extends ElementBaseProps<string>,
    Omit<UploadFileElementProps, 'defaultValue'> {
  mode: 'uploadFile';
}
export interface ICheckBoxElement extends ElementBaseProps, CheckBoxSchemaProps {
  mode: 'checkbox';
}

export interface IMultipleCheckBoxElement extends ElementBaseProps, PropsMultipleCheckBoxElement {
  mode: 'multiple-checkbox';
}

export interface IRadioElement extends ElementBaseProps, RadioSchemaProps {
  mode: 'checkbox';
}
export interface ISelectElement extends ElementBaseProps, PropsSelectElement {
  mode: 'select';
}

export interface IMultipleRadioElement extends ElementBaseProps, MultipleRadioSchemaProps {
  mode: 'multiple-radio';
}

export interface ISwitchElement extends ElementBaseProps<boolean>, SwitchSchemaProps {
  mode: 'switch';
}
export interface IDatePickerElement extends ElementBaseProps<string>, DatePickerSchemaProps {
  mode: 'datePicker';
}
export interface IDateTimePickerElement
  extends ElementBaseProps<string>,
    DateTimePickerSchemaProps {
  mode: 'dateTimePicker';
}
export interface ITimePickerElement extends ElementBaseProps<string>, PropsTimePickerElement {
  mode: 'timePicker';
}
export interface IDateRangePickerElement
  extends ElementBaseProps<string>,
    DateRangePickerSchemaProps {
  mode: 'dateRange';
}

export interface IAutoCompleteElement<T extends FieldValues = FieldValues>
  extends ElementBaseProps<T>,
    Omit<AutoCompleteSchemaProps, 'defaultValue'> {
  mode: 'auto-complete';
}
export interface ITextEditorElement extends ElementBaseProps, TextEditorElementProps {
  mode: 'text-editor';
}

export type ElementFormProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> =
  | ElementBaseProps
  | IDropZoneElement
  | IHiddenElement
  | IAutoCompleteElement<T[TName]>
  | ITextFieldElement<T[TName]>
  | ITextElement
  | IUploadFileElement
  | ICheckBoxElement
  | IMultipleCheckBoxElement
  | IRadioElement
  | ISelectElement
  | IMultipleRadioElement
  | ISwitchElement
  | IDatePickerElement
  | IDateTimePickerElement
  | ITimePickerElement
  | IDateRangePickerElement
  | IButtonElement
  | ITextEditorElement
  | INodeElement;

type SchemaTypeTmp<T extends FieldValues = FieldValues> =
  | {
      [K in FieldPath<T>]: ElementFormProps<T, K> | IRawElement<T, K>;
    }
  | {
      [K in FieldArrayPath<T>]: IArrayElement<T, K>;
    }
  | {
      [key: string]: ElementFormProps<T>;
    };

export type SchemaType<T extends FieldValues = FieldValues> = SchemaTypeTmp<T>;

/*---------------------------*/

export interface FormProps extends ReturnType<typeof useGeneralHook>, FieldValues {}

export type ISchemaFields<
  T extends FieldValues = FieldValues,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> = (params: {
  methods: UseFormReturn<R>;
  fieldName?: string;
  formProps: F;
  valuesField: T;
}) => SchemaType<T>;

export interface FieldsElementType<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> {
  fields: ElementFormProps<T, TName>[];
  id: string;
  title?: ReactNode;
  paper?: boolean;
  paperProps?: PaperProps;
  propsWrapper?: GridProps;
  propsGridContainer?: GridProps;
  hidden?: boolean;
  [key: string]: any;
}
export type IUIKey = string;

export interface IUIField<T extends FieldValues = FieldValues, K extends IUIKey = string> {
  id: K;
  fields?: (keyof T)[] | (string | undefined)[];
  title?: ReactNode;
  paper?: boolean;
  paperProps?: PaperProps;
  propsWrapper?: GridProps;
  propsGridContainer?: GridProps;
  hidden?: boolean;
  [key: string]: any;
}

export type IUIFieldFnc<
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> = (params: { methods: UseFormReturn<R>; formProps: F; valuesField?: any }) => IUIField<T, K>[];

export type IUIFieldSchema<
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> = IUIFieldFnc<T, K, F, R> | IUIField<T, K>[];

type ILayoutFieldParam<T extends FieldValues, Keys extends IUIKey = string> = {
  [K in Keys]: Record<keyof T, ReactNode> & Record<string, ReactNode>;
};

interface ILayoutParams<
  T extends FieldValues,
  K extends IUIKey = string & 'default',
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> {
  valuesField: R;
  fields: ILayoutFieldParam<R, K>;
  methods: UseFormReturn<R>;
  formProps: F;
  view: ReactNode;
  listElement: ReactNode[];
}

type ILayoutFields<
  T extends FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> = (params: ILayoutParams<T, K, F, R>) => ReactNode;

export interface ISchemaForm<
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> {
  // propsWrapperContainer?: GridProps;
  propsGridContainer?: GridProps;
  fields: ISchemaFields<T, F, R> | SchemaType<T>;
  ui?: IUIFieldSchema<T, K, F, R>;
  layout?: ILayoutFields<T, K, F, R>;
  changeDataBeforeSubmit?: (values: T, props: F) => any;
}

/*---------------------------*/
declare const $NestedValue: unique symbol;

type NestedValue<TValue extends object = object> = {
  [$NestedValue]: never;
} & TValue;

export type FieldValues = Record<string, any>;

export type DeepPartial<T> = T extends BrowserNativeObject | NestedValue
  ? T
  : {
      [K in keyof T]?: DeepPartial<T[K]>;
    };

type AsyncDefaultValues<T> = (payload?: unknown) => Promise<T>;

export type DefaultValues<T> =
  T extends AsyncDefaultValues<T> ? DeepPartial<Awaited<T>> : DeepPartial<T>;

export interface PropsForm<
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> {
  schema?: ISchemaForm<T, K, F, R>;
  formData?: T;
  onChange?: (value: T, methods: UseFormReturn<T>) => void;
  formHookProps?: UseFormProps<T>;
  formId?: string;
  fieldName?: FieldPath<T>;
  onSubmit?: (data: any, methods: UseFormReturn<T>) => void | Promise<void>;
  methods?: UseFormReturn<T>;
  hiddenField?: FieldValues;
  notForm?: boolean;
  loading?: boolean;
  formProps?: BoxProps;
  showConfirmBeforeLeave?: boolean | React.ReactNode;
  propsGridContainer?: GridProps;
  globalProps?: FieldValues;
  [key: string]: any;
  initialState?: any;
}

export const mergeFieldName = (params: { name: string; parent?: string; index?: number }): any => {
  const { name, parent, index } = params;
  if (parent && typeof index === 'number') {
    return `${parent}.${index}.${name}`;
  }
  if (parent) {
    return `${parent}.${name}`;
  }
  if (typeof index === 'number') {
    return `${name}.${index}`;
  }
  return name;
};

/*---------------------------*/
export const submitForm = (id: string) => {
  const submitBtn = document.getElementById(id);
  if (submitBtn && submitBtn.tagName === 'INPUT' && submitBtn.getAttribute('type') === 'submit') {
    submitBtn.click();
  }
};

export const validatePhoneNumber = (value: string, intl: any) => {
  return value
    ? countryphonecodes.find((v) => v.code === value)
      ? intl.formatMessage({ id: 'required' })
      : E164_REGEX.test(value)
        ? true
        : intl.formatMessage({ id: 'invalidPhoneNumber' })
    : intl.formatMessage({ id: 'required' });
};
